import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HoldReason } from "../../documentTypes/interfaces";
import { getInLanguageOrDefault } from "../../common/utilities/language";

const useHoldReasonModal = (
  reasons: Array<HoldReason>,
  currentReason: HoldReason | undefined,
  onClose: () => void,
  onConfirm: (holdReason?: HoldReason) => void,
) => {
  const {
    i18n: { language },
  } = useTranslation();

  const currentReasonId = currentReason?.id;

  const [reasonId, setReasonId] = useState<string | undefined>(currentReasonId);

  // A reason deleted after this hold started stays selectable, so reopening the modal does
  // not silently clear it. One missing while not deleted belongs to another document type
  // and is not offered here.
  const selectableReasons = useMemo(() => {
    if (
      !currentReason ||
      !currentReason.deleted ||
      reasons.some((reason) => reason.id === currentReason.id)
    ) {
      return reasons;
    }

    return [...reasons, currentReason];
  }, [reasons, currentReason]);

  const options = useMemo(
    () =>
      selectableReasons.map((reason) => ({
        value: reason.id,
        label: getInLanguageOrDefault(reason.translations, language),
      })),
    [selectableReasons, language],
  );

  // Empty rather than stale when the document type changed under the modal.
  const selectedId = selectableReasons.some((reason) => reason.id === reasonId)
    ? reasonId
    : undefined;

  const handleClose = useCallback(() => {
    setReasonId(currentReasonId);
    onClose();
  }, [currentReasonId, onClose]);

  const handleConfirm = useCallback(() => {
    onConfirm(selectableReasons.find((reason) => reason.id === selectedId));
  }, [onConfirm, selectableReasons, selectedId]);

  return {
    reasonId: selectedId,
    setReasonId,
    options,
    handleClose,
    handleConfirm,
  };
};

export default useHoldReasonModal;
